import { Box, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useMarketplaceStore } from "../../../store/useMarketplaceStore";

const COUNTRY_TIMEZONES: Record<string, string> = {
  US: "America/New_York",
  CA: "America/Toronto",
  MX: "America/Mexico_City",
  BR: "America/Sao_Paulo",
  GB: "Europe/London",
  UK: "Europe/London",
  DE: "Europe/Berlin",
  FR: "Europe/Paris",
  IT: "Europe/Rome",
  ES: "Europe/Madrid",
  NL: "Europe/Amsterdam",
  SE: "Europe/Stockholm",
  PL: "Europe/Warsaw",
  TR: "Europe/Istanbul",
  AE: "Asia/Dubai",
  SA: "Asia/Riyadh",
  IN: "Asia/Kolkata",
  JP: "Asia/Tokyo",
  SG: "Asia/Singapore",
  AU: "Australia/Sydney",
  EG: "Africa/Cairo",
  ZA: "Africa/Johannesburg",
  BE: "Europe/Brussels",
};

function getTimezone(countryCode: string | undefined): string {
  if (!countryCode) return Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    COUNTRY_TIMEZONES[countryCode.toUpperCase()] ??
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
}

function formatTime(
  date: Date,
  timezone: string,
): { time: string; tz: string } {
  const time = new Intl.DateTimeFormat("tr-TR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: timezone,
  }).format(date);

  const tz =
    new Intl.DateTimeFormat("en", {
      timeZoneName: "short",
      timeZone: timezone,
    })
      .formatToParts(date)
      .find((p) => p.type === "timeZoneName")?.value ?? "";

  return { time, tz };
}

type MarketplaceClockProps = {
  /**
   * "chip": compact flag+time pill (default, used in the header/mobile).
   * "bare": just the time text, no container — for tight inline spots.
   */
  variant?: "chip" | "bare";
};

export const MarketplaceClock = ({
  variant = "chip",
}: MarketplaceClockProps) => {
  const selectedCountry = useMarketplaceStore((s) => s.selectedCountry);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const timezone = getTimezone(selectedCountry?.countryCode);
  const { time, tz } = formatTime(now, timezone);
  const countryCode = selectedCountry?.countryCode?.toUpperCase() ?? "—";

  if (variant === "bare") {
    return (
      <Tooltip title={`${timezone} (${tz})`} placement="bottom">
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{
            fontVariantNumeric: "tabular-nums",
            letterSpacing: "0.02em",
            cursor: "default",
            userSelect: "none",
            flexShrink: 0,
          }}
        >
          {time}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={timezone} placement="bottom">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          borderRadius: 1.5,
          bgcolor: "action.hover",
          cursor: "default",
          userSelect: "none",
          minWidth: 110,
        }}
      >
        {selectedCountry?.flagUrl && (
          <Box
            component="img"
            src={`${selectedCountry.flagUrl}?width=20`}
            alt={countryCode}
            sx={{
              width: 20,
              height: 14,
              objectFit: "cover",
              borderRadius: 0.25,
              flexShrink: 0,
            }}
          />
        )}
        <Box>
          <Typography
            variant="caption"
            component="div"
            fontWeight={700}
            lineHeight={1.1}
            sx={{ fontVariantNumeric: "tabular-nums", letterSpacing: "0.02em" }}
          >
            {time}
          </Typography>
          <Typography
            variant="caption"
            component="div"
            color="text.secondary"
            lineHeight={1.1}
            sx={{ fontSize: 10 }}
          >
            {tz}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};
